import { useAuthContext } from "../../auth/state/AuthContext";
import { ReactComponent as Shipside } from "../../shared/assets/ship-side.svg";
import { themeAtom } from "../../shared/atoms";
import { VoyageVesselInfo } from "../../tech-form/models/FormInterfaces";
import { datadogRum } from "@datadog/browser-rum";
import { CalculatorIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface VesselInformationProps {
  vesselInfo: VoyageVesselInfo | null;
  className?: string;
}

const VesselInformation: React.FC<VesselInformationProps> = ({ vesselInfo, className }) => {
  const theme = useAtomValue(themeAtom);

  const vesselFields: {
    label: string;
    getValue: (info: VoyageVesselInfo) => string;
  }[] = [
    { label: "Vessel name", getValue: (info) => info.VESSEL_NAME || "N/A" },
    {
      label: "Vessel code",
      getValue: (info) => (info.VESSEL_CODE !== undefined ? info.VESSEL_CODE.toString() : "N/A"),
    },
    { label: "IMO Number", getValue: (info) => (info.IMO_NUMBER !== undefined ? info.IMO_NUMBER.toString() : "N/A") },
    // There are fields like this one that we want to show the label but the backend doesn't return any data
    { label: "Owner", getValue: (info) => info.OWNER || "N/A" },
    { label: "Year built", getValue: (info) => (info.YEAR_BUILT !== undefined ? info.YEAR_BUILT.toString() : "N/A") },
    { label: "Vessel type", getValue: (info) => info.VESSEL_TYPE || "N/A" },
    { label: "Class society", getValue: (info) => info.CLASS_SOCIET_YCODE ?? "N/A" },
    { label: "Vessel email", getValue: (info) => info.EMAIL || "N/A" },
    {
      label: "Summer DWT",
      getValue: (info) => (info.DWT !== undefined ? `${info.DWT.toLocaleString("en-US")} mt` : "N/A"),
    },
    {
      label: "Summer Draft",
      getValue: (info) =>
        info.VESSEL_SUMMER_DRAFT !== undefined ? `${info.VESSEL_SUMMER_DRAFT.toLocaleString("en-US")} m` : "N/A",
    },
    {
      label: "TPC",
      getValue: (info) => (info.TPC !== undefined ? `${info.TPC} mt/cm` : "N/A"),
    },
    {
      label: "Grain Capacity",
      getValue: (info) =>
        info.CAPACITY_GRAIN_M3 !== undefined && info.CAPACITY_GRAIN_FT3 !== undefined
          ? `${info.CAPACITY_GRAIN_M3.toLocaleString("en-US")} m3 (${info.CAPACITY_GRAIN_FT3.toLocaleString("en-US")} ft3)`
          : "N/A",
    },
    {
      label: "Bale Capacity",
      getValue: (info) =>
        info.CAPACITY_BALE_M3 !== undefined && info.CAPACITY_BALE_FT3 !== undefined
          ? `${info.CAPACITY_BALE_M3.toLocaleString("en-US")} m3 (${info.CAPACITY_BALE_FT3.toLocaleString("en-US")} ft3)`
          : "N/A",
    },
    { label: "Scrubber type", getValue: (info) => info.SCRUBBER_TYPE || "N/A" },
    { label: "Neo Panama Fitted (Panamax only)", getValue: () => "N/A" },
  ];
  const { sub: email } = useAuthContext().state.user?.accessToken || {};

  function trackGoToIntakeCalc() {
    datadogRum.addAction("trackIntakeCalc", {
      ext_user_email: email,
    });
  }

  return (
    <div className={twMerge("flex flex-col gap-5 pt-4", className)}>
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <p className={twMerge("h-8 w-8 rounded-full p-2", theme.myVesselsTable.tailwind.emptyStateIconBg)}>
            <Shipside />
          </p>
          <div className={twMerge("text-lg font-bold", theme.home.tailwind.textDefault)}>Vessel Information</div>
        </div>

        <div>
          <div className="grid grid-cols-2 gap-6">
            {vesselFields.map(({ label, getValue }) => (
              <InfoBadge key={label} title={label} value={vesselInfo ? getValue(vesselInfo) : "N/A"} />
            ))}
          </div>
        </div>
      </div>

      {!!vesselInfo?.IMO_NUMBER && !!vesselInfo?.VESSEL_CODE && !!vesselInfo?.VESSEL_NAME && (
        <Link
          to={`/vessel/details/${vesselInfo?.IMO_NUMBER}/${vesselInfo?.VESSEL_CODE}/${vesselInfo?.VESSEL_NAME}`}
          className={twMerge(
            "self-start rounded border px-4 py-2 text-sm",
            theme.uwhcAssistant.border,
            theme.headerComponent.notifications.iconBackground,
            theme.home.tailwind.spinner,
          )}
        >
          View details
        </Link>
      )}
      <div className="flex items-center self-start">
        <CalculatorIcon className={twMerge("mr-1 h-4 w-4", theme.myVesselsTableRow.tableText.secondary)} />
        <a
          href="https://loadicator.cglcloud.com/"
          target="_blank"
          rel="noreferrer"
          className="text-gray-400"
          onClick={trackGoToIntakeCalc}
        >
          Cargo Intake Calculator
        </a>
      </div>
    </div>
  );
};

interface InfoBadgeProps {
  title: string;
  value: React.ReactNode;
}

const InfoBadge: React.FC<InfoBadgeProps> = ({ title, value }) => {
  const theme = useAtomValue(themeAtom);

  return (
    <div className="flex flex-col gap-3">
      <span className={twMerge("text-xs font-normal", theme.vesselDetails.info)}>{title}:</span>
      <span
        className={twMerge("break-words text-sm font-normal", theme.uwhcAssistant.theadText)}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </span>
    </div>
  );
};

export default VesselInformation;
